import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Calendar, Tag } from 'lucide-react';

const ChangelogPage = () => {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['publicChangelog'],
    queryFn: async () => {
      const res = await axiosClient.get('/public/changelog');
      return Array.isArray(res) ? res : (res.data || []);
    }
  });

  
  const renderMarkdown = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((line, index) => {
        // 1. Headers (## Heading)
        if (line.trim().startsWith('##')) {
            return (
                <h3 key={index} className="text-lg font-bold text-white mt-6 mb-3 border-b border-slate-800 pb-1">
                    {line.replace(/^##\s*/, '')}
                </h3>
            );
        }
        
        // 2. Bullet Points (* Item or - Item)
        if (line.trim().startsWith('-') || line.trim().startsWith('*')) {
            const content = line.replace(/^[-*]\s*/, '');
            
            // Handle **Bold** inside list items
            const parts = content.split('**');
            return (
                <li key={index} className="flex items-start text-slate-300 mb-2 leading-relaxed text-sm">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>
                        {parts.map((part, i) => 
                            // Odd indices are bold (between ** and **)
                            i % 2 === 1 ? <strong key={i} className="text-white font-semibold">{part}</strong> : part
                        )}
                    </span>
                </li>
            );
        }

        // 3. Normal Text (Empty lines or paragraphs)
        if (line.trim() === '') {
            return <div key={index} className="h-2"></div>;
        }

        return <p key={index} className="text-slate-400 text-sm mb-2">{line}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-[#020617] pt-24 pb-20 px-6 font-sans text-white">
       <div className="max-w-4xl mx-auto">
        
        <div className="text-center mb-16 space-y-4">
            <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                Product <span className="text-teal-500">Changelog</span>
            </h1>
            <p className="text-slate-400 text-lg">Track the evolution of the AIRS Security Engine.</p>
        </div>

        {isLoading ? (
            <div className="text-center text-slate-500 py-20">Loading Updates...</div>
        ) : (
            <div className="space-y-12 border-l-2 border-slate-800 ml-4 pl-8 md:ml-8 md:pl-10 relative">
                {logs.map((log, idx) => (
                    <div key={idx} className="relative">
                        {/* Timeline Dot */}
                        <div className="absolute -left-[45px] md:-left-[51px] top-0 w-6 h-6 bg-[#020617] border-4 border-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></div>
                        
                        <div className="bg-[#0f172a] border border-slate-800 p-8 rounded-2xl shadow-sm hover:border-blue-500/30 transition-all duration-300 group">
                            
                            {/* Version Header */}
                            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4 border-b border-slate-800/50 pb-4">
                                <div className="flex items-center gap-3">
                                    <span className="bg-blue-600 text-white px-3 py-1 rounded-lg text-lg font-bold shadow-lg shadow-blue-500/20">
                                        {log.version}
                                    </span>
                                    {idx === 0 && (
                                        <span className="text-[10px] font-bold bg-green-500/10 text-green-400 px-2 py-0.5 rounded border border-green-500/20 uppercase tracking-wide">
                                            Latest Release
                                        </span>
                                    )}
                                </div>
                                <span className="text-xs text-slate-500 flex items-center font-mono bg-slate-900 px-3 py-1 rounded-full border border-slate-800">
                                    <Calendar className="w-3 h-3 mr-2 text-teal-500" /> 
                                    {new Date(log.created_at || log.release_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                </span>
                            </div>


                            <div className="prose prose-invert max-w-none">
                                <ul className="list-none pl-0 space-y-1">
                                    {renderMarkdown(log.release_notes || log.description)}
                                </ul>
                            </div>

                            {/* Download Button (Optional) */}
                            {log.download_url && (
                                <div className="mt-6 pt-4 border-t border-slate-800/50 flex justify-end">
                                    <a 
                                        href={log.download_url} 
                                        target="_blank" 
                                        rel="noreferrer"
                                        className="text-xs font-bold text-teal-400 hover:text-white flex items-center transition-colors"
                                    >
                                        <Tag className="w-3 h-3 mr-1" /> Download Source
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>
    </div>
  );
};

export default ChangelogPage;